import { PanelWindow as WindowDefinition } from "@dextall/panel-hooks-editor-2d";
import {
    type SystemSettings,
    type WindowOverlapsSetting,
    getWindowOverlapsSetting,
} from "../../../responses/systemSettings";
import { BoundaryLoopsFactory } from "../boundaries/boundaryLoopsFactory";
import {
    CladdingCellExtrusionGeometryFactory,
    ExtrusionGeometry,
} from "../geometry/claddingCellExtrusionGeometryFactory";
import { PanelWindowCell } from "./panelWindowCell";
import { PanelWindowGlass } from "./panelWindowGlass";
import { PanelWindowSill } from "./panelWindowSill";
import { createWindowSourceDefinition } from "./panelWindowSourceDefinitionFactory";
import type { CladdingCellStyle, IPanelSourceOpening } from "../../../responses/panelSource";

export class PanelWindow {
    constructor(
        public readonly definition: WindowDefinition,
        private readonly windowGlass: PanelWindowGlass,
        private readonly windowSill: PanelWindowSill,
        private readonly windowCells: PanelWindowCell[],
        private readonly panelWindowDefinition: IPanelSourceOpening,
        private readonly systemSetting: SystemSettings,
    ) {}

    get openingWidth(): number {
        return this.panelWindowDefinition.box.max.x - this.panelWindowDefinition.box.min.x;
    }

    get openingHeight(): number {
        return this.panelWindowDefinition.box.max.y - this.panelWindowDefinition.box.min.y;
    }

    get frameWidth(): number {
        const { windowFrameOffset } = this.systemSetting;
        return this.openingWidth - windowFrameOffset.left - windowFrameOffset.right;
    }

    get frameHeight(): number {
        const { windowFrameOffset } = this.systemSetting;
        return this.openingHeight - windowFrameOffset.top - windowFrameOffset.bottom;
    }

    /** Manufacturer's specification area in square feet */
    get area(): number {
        return this.panelWindowDefinition.area;
    }

    set area(value: number) {
        this.panelWindowDefinition.area = value;
    }

    /** Weight in kilograms */
    get weight(): number {
        return this.panelWindowDefinition.weight;
    }

    set weight(value: number) {
        this.panelWindowDefinition.weight = value;
    }

    static create(
        panelOrigin: THREE.Vector3,
        panelWindowDefinition: IPanelSourceOpening,
        systemSetting: SystemSettings,
        transform: THREE.Matrix4,
        claddingExtrusionFactory: CladdingCellExtrusionGeometryFactory,
        boundaryLoopsFactory: BoundaryLoopsFactory,
        glassMaterial: THREE.Material,
        panelStyle: CladdingCellStyle,
    ): PanelWindow {
        const windowOverlapsSetting = getWindowOverlapsSetting(systemSetting, panelStyle);
        const glassBoundaryLoop = boundaryLoopsFactory.createLoopFromBox(
            createGlassBox(panelWindowDefinition, windowOverlapsSetting),
            transform,
        );

        const windowGlass = new PanelWindowGlass(glassBoundaryLoop, glassMaterial, claddingExtrusionFactory);
        const windowSill = PanelWindowSill.createWindowSill(
            panelWindowDefinition,
            systemSetting,
            transform,
            boundaryLoopsFactory,
            claddingExtrusionFactory,
            panelStyle,
        );
        const windowCells: PanelWindowCell[] = [];
        for (const cell of panelWindowDefinition.cells) {
            windowCells.push(
                new PanelWindowCell(
                    panelWindowDefinition,
                    cell,
                    systemSetting,
                    transform,
                    true,
                    boundaryLoopsFactory,
                    claddingExtrusionFactory,
                    panelStyle,
                ),
            );
            windowCells.push(
                new PanelWindowCell(
                    panelWindowDefinition,
                    cell,
                    systemSetting,
                    transform,
                    false,
                    boundaryLoopsFactory,
                    claddingExtrusionFactory,
                    panelStyle,
                ),
            );
        }

        const definition = createWindowSourceDefinition(panelOrigin, panelWindowDefinition, systemSetting);

        return new PanelWindow(definition, windowGlass, windowSill, windowCells, panelWindowDefinition, systemSetting);
    }

    createGeometry(): ExtrusionGeometry[] {
        return [
            this.windowGlass.createGeometry(),
            this.windowSill.createGeometry(),
            ...this.windowCells.map(x => x.createGeometry()),
        ];
    }
}

const createGlassBox = (
    panelWindowDefinition: IPanelSourceOpening,
    windowOverlaps: WindowOverlapsSetting,
): THREE.Box3 => {
    return new THREE.Box3(
        new THREE.Vector3(panelWindowDefinition.box.min.x + windowOverlaps.left, panelWindowDefinition.box.min.y, 0),
        new THREE.Vector3(
            panelWindowDefinition.box.max.x - windowOverlaps.right,
            panelWindowDefinition.box.max.y - windowOverlaps.top,
            0,
        ),
    );
};
